import React, { useRef, useState, useCallback, useEffect } from "react";
import Header from "../components/Header";
import CropModal from "../components/CropModal";
import CameraModal from "../components/CameraModal";
import { getCroppedImg, dataURLtoBlob } from "../utils/cropImage";
import { isIOS } from "../utils/deviceDetection";
import axiosInstance from "../utils/axiosInstance";
import Session from "../utils/sessionStorage";
import { useLocation, useNavigate } from "react-router-dom";
import Prompt from "../components/Prompt";
import ErrorModal from "../components/ErrorModal";
import loadingimage from "../assets/gifs/loading (1).gif";
import SuccessModal from "../components/SuccessModal";

const MNPReg = () => {
    const location = useLocation();
    const { selectedDonor } = location.state || {};
    console.log("selected donor in MNPReg: ", selectedDonor);
    const navigate = useNavigate();
    const fileInputRef = useRef(null);
    const [rawImage, setRawImage] = useState(null);
    const [croppedImage, setCroppedImage] = useState(null);
    const [filename, setFilename] = useState("");
    const [documentType, setDocumentType] = useState('passport');
    const [cropModalOpen, setCropModalOpen] = useState(false);
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [rotation, setRotation] = useState(0);
    const [croppedPixels, setCroppedPixels] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [mykadFrontData, setMykadFrontData] = useState(null);
    const [mykadBackData, setMykadBackData] = useState(null);
    const [currentStep, setCurrentStep] = useState('select');
    const [extractedData, setExtractedData] = useState(null);
    const [showError, setShowError] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [infoMessage, setInfoMessage] = useState("");
    const [cameraModalOpen, setCameraModalOpen] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const result = Session.get("_user_");
    const userData = result ? JSON.parse(result) : "";
    const [sttdUserPrompt, setSttdUserPrompt] = useState(false);
    console.log("session user :", userData.customerType);
    const stduser = userData?.customerType;
    useEffect(() => {
        if (stduser === "StdCDD") {
            setSttdUserPrompt(true);
        }
    }, [stduser]);

    const handleDocumentTypeSelect = useCallback((type) => {
        setDocumentType(type);
        setRawImage(null);
        setCroppedImage(null);
        setCroppedPixels(null);
        setCurrentStep(type === 'mykad' ? 'front' : 'select');
        // Reset MyKad data when switching document types
        if (type === 'passport') {
            setMykadFrontData(null);
            setMykadBackData(null);
        }
    }, []);

    const handleScanClick = useCallback(() => {
        // Open camera modal for non-iOS devices, use native camera for iOS
        if (isIOS()) {
            // Directly trigger the native camera via file input on iOS
            if (fileInputRef.current) {
                fileInputRef.current.click();
            }
        } else {
            // Open in-screen camera modal for non-iOS devices
            setCameraModalOpen(true);
        }
    }, []);

    const handleFileChange = useCallback((e) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = (event) => {
                setRawImage(event.target.result);
                setFilename(`${documentType}_${Date.now()}.jpeg`);
                setCropModalOpen(true);
            };
            reader.readAsDataURL(file);
        }
    }, [documentType]);

    const handleCameraCapture = useCallback((imageSrc) => {
        setRawImage(imageSrc);
        setFilename(`${documentType}_${Date.now()}.jpeg`);
        setCameraModalOpen(false);
        setCropModalOpen(true);
    }, [documentType]);

    const onCropComplete = useCallback((_, croppedAreaPixels) => {
        setCroppedPixels(croppedAreaPixels);
    }, []);




    const token = Session.get("token");
    const handleExtractData = useCallback(async () => {
        if (!croppedImage || !documentType) return;

        setIsSubmitting(true);

        try {
            if (croppedImage === 'data:,' || croppedImage.length < 100) {
                throw new Error('Invalid image data');
            }

            let blob = dataURLtoBlob(croppedImage);
            if (!blob) {
                blob = await fetch(croppedImage).then(res => res.blob());
            }

            if (!blob || blob.size === 0) {
                throw new Error('Failed to convert image to blob');
            }

            if (blob.size > 5 * 1024 * 1024) {
                console.warn('Image size is large:', blob.size / 1024 / 1024, 'MB');
            }

            const file = new File([blob], filename, { type: 'image/jpeg' });
            const formData = new FormData();

            let docTypeToSend = documentType;
            if (documentType === 'mykad') {
                docTypeToSend = currentStep === 'front' ? 'mykad_front' : 'mykad_back';
            }

            formData.append('doc_type', docTypeToSend);
            formData.append('image', file);

            const response = await axiosInstance.post('api/v1/extract-data', formData, {
                headers: {
                    "Content-Type": 'multipart/form-data',
                    Authorization: `Bearer ${token}`,
                },
                timeout: 30000,
            });

            if (!response.data) {
                throw new Error('Empty response from server');
            }

            if (documentType === 'passport') {
                setExtractedData(response.data);
                navigate('/mnp-register-form', {
                    state: {
                        extractedData: response.data,
                        documentType,
                        selectedDonor
                    }
                });
            } else if (documentType === 'mykad') {
                if (currentStep === 'front') {
                    setMykadFrontData(response.data);
                    setCurrentStep('back');
                    setRawImage(null);
                    setCroppedImage(null);
                    setInfoMessage("Please take a photo of the back side of your MyKad");
                    setShowModal(true);
                } else if (currentStep === 'back') {
                    setMykadBackData(response.data);
                    navigate('/mnp-register-form', {
                        state: {
                            extractedData: { front: mykadFrontData, back: response.data },
                            documentType,
                            selectedDonor
                        }
                    });
                }
            }

        } catch (error) {
            console.error('Extract data error:', error);
            let errorMsg = "Failed to extract data";
            if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
                errorMsg = "Request timeout. Please check your connection and try again.";
            } else if (error.response?.status === 413) {
                errorMsg = "Image is too large. Please use a smaller image and try again.";
            } else if (error.response?.status === 400) {
                errorMsg = error.response.data?.message || "Invalid image format or data. Please try again.";
            } else if (error.response?.data?.message) {
                errorMsg = error.response.data.message;
            } else if (error.message) {
                errorMsg = error.message;
            }
            setErrorMessage(errorMsg);
            setShowError(true);
            setRawImage(null);
            setCroppedImage(null);
            setCroppedPixels(null);
        } finally {
            setIsSubmitting(false);
        }
    }, [croppedImage, documentType, filename, token, currentStep, navigate, mykadFrontData, selectedDonor]);


    const applyCrop = useCallback(async () => {
        if (rawImage && croppedPixels) {
            try {
                const cropped = await getCroppedImg(rawImage, croppedPixels, rotation);
                if (!cropped || cropped === 'data:,') {
                    throw new Error('Failed to generate cropped image');
                }
                setCroppedImage(cropped);
                setCropModalOpen(false);
                setTimeout(() => {
                    handleExtractData();
                }, 300);
            } catch (error) {
                console.error('Cropping error:', error);
                setErrorMessage(error.message || 'Failed to crop image. Please try again.');
                setShowError(true);
            }
        }
    }, [rawImage, croppedPixels, rotation, handleExtractData]);

    const reset = () => {
        setRawImage(null);
        setCroppedImage(null);
        setCropModalOpen(false);
        setRotation(0);
        setZoom(1);
        setCrop({ x: 0, y: 0 });
    };
    if (!sttdUserPrompt) {
        return <Prompt call={"onLink"} close={() => navigate('/home')} />
    }

    return (
        <>
            <Header />
            <div className="max-w-md mx-auto min-h-[calc(100vh-64px)] flex flex-col">
                <div className="px-4 flex-1">
                    {/* Two-button toggle for document type */}
                    {!cameraModalOpen && (
                        <div className="flex  rounded-full bg-[#1111110D] mt-4 mb-6">
                            <button
                                className={`w-1/2 py-3 text-[15px] font-semibold ${documentType === 'passport'
                                    ? 'bg-primary text-white rounded-full'
                                    : 'text-gray rounded-full'
                                    }`}
                                onClick={() => handleDocumentTypeSelect('passport')}
                            >
                                Passport
                            </button>
                            <button
                                className={`w-1/2 py-3 text-[15px] font-semibold ${documentType === 'mykad'
                                    ? 'bg-primary text-white rounded-full'
                                    : 'text-gray rounded-full'
                                    }`}
                                onClick={() => handleDocumentTypeSelect('mykad')}
                            >
                                MyKad
                            </button>
                        </div>
                    )}

                    {!croppedImage && !cameraModalOpen && (
                        <>
                            <h2 className="text-sm font-semibold px-2 mt-6">User Validation (Identity)</h2>
                            <p className="text-xs text-gray-500 px-2">
                                {documentType === "passport"
                                    ? "Please take a clear photo of your passport."
                                    : documentType === "mykad"
                                        ? currentStep === 'front'
                                            ? "Please take a clear photo of the FRONT side of your MyKad."
                                            : "Please take a clear photo of the BACK side of your MyKad."
                                        : "Please select document type to begin."}
                            </p>

                            {/* Instructions section with passport/mykad icon */}
                            <div className="py-6 px-2 mb-4">
                                <div className="flex justify-center mb-4">
                                    <div className="w-full h-[249px] bg-primary/5 rounded-[24px] flex items-center justify-center">
                                        <svg width="140" height="140" viewBox="0 0 140 140" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M120 120H140M120 140V120V140ZM20 120H0H20ZM20 140V120V140ZM120 20H140H120ZM120 0V20V0ZM20 20H0H20ZM20 0V20V0Z" stroke="#3E3E91" strokeWidth="3" />
                                            <rect x="30" y="35" width="80" height="70" rx="4" stroke="#3E3E91" strokeWidth="2.5" />
                                            <path d="M45 50H95M45 60H95M45 70H75M45 80H85M45 90H65" stroke="#3E3E91" strokeWidth="2" strokeLinecap="round" />
                                            <circle cx="70" cy="55" r="8" stroke="#3E3E91" strokeWidth="2" />
                                        </svg>
                                    </div>
                                </div>

                                <div className="space-y-3 text-sm">
                                    <div className="text-[15px]">
                                        <p className="font-semibold  mb-1">1. Do not cover any part of your {documentType === 'passport' ? 'passport' : 'MyKad'}.</p>
                                        <p className="text-gray pl-3">Make sure your hand or fingers are not blocking any text or photo.</p>
                                    </div>
                                    <div className="text-[15px]">
                                        <p className="font-semibold mb-1">2. Place your {documentType === 'passport' ? 'passport' : 'MyKad'} on a flat surface.</p>
                                        <p className="text-gray pl-3  ">Ensure the entire {documentType === 'passport' ? 'passport page' : 'card'} is clearly visible.</p>
                                    </div>
                                    <div className="text-[15px]">
                                        <p className="font-semibold mb-1">3. Ensure the image is clear and in focus.</p>
                                        <p className="text-gray pl-3">Blurry images may be rejected.</p>
                                    </div>
                                    <div className="text-[15px]">
                                        <p className="font-semibold mb-1">4. Use original documents only</p>
                                        <p className="text-gray pl-3">Do not upload photocopies or screenshots.</p>
                                    </div>
                                </div>
                            </div>
                        </>
                    )}

                    {/* Show captured image if exists */}
                    {croppedImage && (
                        <div className="mb-4">
                            <img src={croppedImage} alt="Captured document" className="w-full rounded-lg" />
                        </div>
                    )}

                    {/* Hidden file input for native camera */}
                    <input
                        type="file"
                        ref={fileInputRef}
                        onChange={handleFileChange}
                        accept="image/*"
                        capture="environment"
                        style={{ display: 'none' }}
                    />

                    {/* Camera Modal */}
                    <CameraModal
                        isOpen={cameraModalOpen}
                        onRequestClose={() => setCameraModalOpen(false)}
                        onCapture={handleCameraCapture}
                    />

                    {/* Crop Modal */}
                    <CropModal
                        isOpen={cropModalOpen}
                        rawImage={rawImage}
                        crop={crop}
                        zoom={zoom}
                        rotation={rotation}
                        onCropChange={setCrop}
                        onZoomChange={setZoom}
                        onRotationChange={setRotation}
                        onCropComplete={onCropComplete}
                        onClose={() => setCropModalOpen(false)}
                        onConfirm={applyCrop}
                        onRetake={reset}
                    />
                </div>

                {/* Bottom Buttons */}
                <div className="mt-auto bg-white p-4">
                    {/* Scan Button */}
                    {!croppedImage && !cameraModalOpen && (
                        <button
                            onClick={handleScanClick}
                            className="w-full bg-primary text-white py-4 rounded-full text-[12px] font-[400]"
                        >
                            Scan {documentType === 'passport' ? 'Passport' : currentStep === 'front' ? 'MyKad Front' : 'MyKad Back'}
                        </button>
                    )}

                    {/* Continue and Cancel buttons after capture */}
                    {croppedImage && currentStep !== 'complete' && (
                        <>
                            <button
                                onClick={() => {
                                    setRawImage(null);
                                    setCroppedImage(null);
                                    setCroppedPixels(null);
                                }}
                                className="w-full border-2 border-primary text-primary py-[14px] rounded-full text-[12px] font-[400]"
                            >
                                Retake
                            </button>
                            <button
                                onClick={handleExtractData}
                                disabled={isSubmitting}
                                className="mt-2 w-full bg-primary text-white py-4 rounded-full text-[12px] font-[400]"
                            >
                                Continue
                            </button>

                        </>
                    )}
                </div>

                {/* Info Message */}
                {showModal &&
                    <SuccessModal message={infoMessage} close={() => setShowModal(false)} />
                }

                {/* Loading Modal */}
                {isSubmitting && (
                    <div className="overflow-y-auto overflow-x-hidden fixed top-0 right-0 left-0 z-50 flex justify-center items-center h-full w-full md:inset-0 max-h-full bg-black bg-opacity-50">
                        <div className="p-4 w-full max-w-md max-h-full">
                            <div className="px-7 py-6 bg-white rounded-3xl shadow">
                                <div className="flex flex-col gap-2 items-center justify-center pb-7">
                                    <img src={loadingimage} alt="loading" className="w-20 h-20" />
                                    <p className="text-base font-semibold">Processing...</p>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Error Modal */}
                {showError && <ErrorModal close={() => setShowError(false)} message={errorMessage} />}
            </div>
        </>
    )
}

export default MNPReg
